import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import type { CompanyOverview } from '@/types/company';
import type { ConsultantMessage, ThreadWithMessages } from '@/types/messaging';
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface NewConversationDialogProps {
  isPlatformAdminView: boolean;
  activeCompanyId: string | null;
  onCreated: (thread: ThreadWithMessages) => void;
  onClose: () => void;
}

export default function NewConversationDialog({
  isPlatformAdminView,
  activeCompanyId,
  onCreated,
  onClose,
}: NewConversationDialogProps) {
  const t = useTranslations('consultantMessages');
  const [companies, setCompanies] = useState<CompanyOverview[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(isPlatformAdminView);
  const [pickedCompanyId, setPickedCompanyId] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once createThread/sendMessage succeed, so a retry after a later
  // failure (e.g. the final getThread call) reuses the same thread and
  // doesn't resend the first message — each is only ever attempted once per
  // successful outcome, however many times the overall submit is retried.
  const [pendingThreadId, setPendingThreadId] = useState<string | null>(null);
  const [sentMessage, setSentMessage] = useState<ConsultantMessage | null>(null);

  useEffect(() => {
    if (!isPlatformAdminView) return;
    let cancelled = false;
    setLoadingCompanies(true);
    ServiceRegistry.getCompanyService()
      .getAllCompanies()
      .then((result) => {
        if (!cancelled) setCompanies(result);
      })
      .catch(() => {
        if (!cancelled) setError(t('newConversation.companiesFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoadingCompanies(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPlatformAdminView]);

  const companyId = isPlatformAdminView ? pickedCompanyId : activeCompanyId;

  const handleSubmit = async () => {
    const text = message.trim();
    if (!text || !companyId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      let threadId = pendingThreadId;
      if (!threadId) {
        const thread = await ServiceRegistry.getMessagingService().createThread({ companyId });
        threadId = thread.id;
        setPendingThreadId(threadId);
      }
      if (!sentMessage) {
        const sent = await ServiceRegistry.getMessagingService().sendMessage({ threadId, body: text });
        setSentMessage(sent);
      }
      const full = await ServiceRegistry.getMessagingService().getThread(threadId);
      onCreated(full);
    } catch (err) {
      const reason = userFacingErrorMessage(err, t('newConversation.genericReason'));
      setError(t('newConversation.startFailed', { reason }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('newConversation.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isPlatformAdminView && (
            <div className="space-y-1.5">
              <Label htmlFor="new-conversation-company">{t('newConversation.company')}</Label>
              {loadingCompanies ? (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" /> {t('newConversation.loadingCompanies')}
                </div>
              ) : (
                <Select
                  value={pickedCompanyId}
                  onValueChange={(value) => {
                    setPickedCompanyId(value);
                    // A thread/message may already exist for a previously
                    // picked company (see pendingThreadId/sentMessage above) —
                    // switching companies must not silently reuse either, or a
                    // retried send would land in the wrong company's thread.
                    setPendingThreadId(null);
                    setSentMessage(null);
                    setError(null);
                  }}
                >
                  <SelectTrigger id="new-conversation-company">
                    <SelectValue placeholder={t('newConversation.selectCompany')} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="new-conversation-message">{t('newConversation.prompt')}</Label>
            <Textarea
              id="new-conversation-message"
              placeholder={t('newConversation.placeholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px] resize-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !submitting) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('newConversation.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!message.trim() || !companyId || submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" /> {t('newConversation.starting')}
              </>
            ) : (
              t('newConversation.start')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
